import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef, RowSelectionState, StockFeatures } from '@tanstack/react-table';
import type { IssueSummary, IssueCategorySetting, IssueSeverity } from '@chatsdet/types/issues';
import {
  BulkActionBar,
  DataTable,
  EditableBadge,
  Flex,
  TagBadge,
  Text,
  Tooltip,
  TooltipTrigger,
  Warning,
  type EditableBadgeOption,
  type TagColor,
  badgeVariantToTagColor,
} from '@chatsdet/ui';
import { Link as LinkIcon } from '@phosphor-icons/react';
import { API_URL } from '../../../config';
import type { CanonicalPage } from '../../../hooks/useCanonicalPages';
import { usePropertySummaries } from '../../../hooks/usePropertyMetadata';
import {
  issueStatusBadgeVariants,
  formatStatusLabel,
  issueSeverityBadgeVariants,
  formatSeverityLabel,
} from './issueBadges';
import { resolveIssuePageUrl } from './issueUrls';
import { SeverityEditableBadge } from './SeverityEditableBadge';

type CanonicalPageSummary = Pick<CanonicalPage, 'id' | 'url' | 'title'>;

const formatTimestamp = (value: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return formatDistanceToNow(date, { addSuffix: true });
};

const formatExactTimestamp = (value: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString();
};

const wasFirstSeenInLast24Hours = (value: string | null | undefined) => {
  if (!value) return false;
  const date = new Date(value);
  const time = date.getTime();
  if (Number.isNaN(time)) return false;
  const ageMs = Date.now() - time;
  return ageMs >= 0 && ageMs <= 24 * 60 * 60 * 1000;
};

function getUrlPath(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

function getPageLabel(url: string): string {
  try {
    const parsed = new URL(url);
    const path = parsed.pathname && parsed.pathname !== '/' ? parsed.pathname : '';
    const combined = `${parsed.hostname}${path}`;
    return combined.length > 60 ? `${combined.slice(0, 57)}…` : combined || url;
  } catch {
    return url;
  }
}

type BulkSelectionControls = {
  selectedCount: number;
  totalItems: number;
  selectionMode: 'page' | 'all';
  deselectedCount?: number;
  canSelectAllResults?: boolean;
  onSelectAllResults?: () => void;
  onClearSelection: () => void;
  onArchiveSelected: () => void | Promise<void>;
  isArchiveLoading?: boolean;
  onRevalidateSelected?: () => void | Promise<void>;
  isRevalidateLoading?: boolean;
};

type IssueSortField = 'severity' | 'status' | 'category' | 'title' | 'path' | 'lastSeenAt';
type SortOrder = 'asc' | 'desc';

type IssuesTableV2Props = {
  issues: IssueSummary[];
  onIssueClick?: (issue: IssueSummary) => void;
  emptyMessage?: React.ReactNode;
  /** Full replacement for the empty container (e.g. an <EmptyState/>), rendered without the default dashed wrapper. */
  emptyState?: React.ReactNode;
  /** Forwarded to DataTable — lets the filter toolbar lockup remove the top chrome. */
  tableContainerClassName?: string;
  pagination?: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    isLoading?: boolean;
  };
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  enableSelection?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (
    updater: RowSelectionState | ((prev: RowSelectionState) => RowSelectionState)
  ) => void;
  bulkSelection?: BulkSelectionControls;
  sortBy?: IssueSortField;
  sortOrder?: SortOrder;
  onSortChange?: (sortBy: string | undefined, sortOrder: 'asc' | 'desc' | undefined) => void;
  /** Categories for editable category badges. If not provided, category is read-only. */
  categories?: IssueCategorySetting[];
  /** Callback when an issue is mutated (severity/category changed). */
  onIssueMutated?: () => void;
  /** Filter by severity. */
  severityFilter?: IssueSeverity | 'all';
  /** Callback when severity filter changes. */
  onSeverityFilterChange?: (severity: IssueSeverity | 'all') => void;
  /** Filter by category ID. */
  categoryFilter?: string | 'all';
  /** Callback when category filter changes. */
  onCategoryFilterChange?: (categoryId: string | 'all') => void;
  /** ID of the issue to highlight (e.g., the last clicked row). */
  highlightedIssueId?: string | null;
};

// Severity filter options including 'all'
const severityFilterOptions: EditableBadgeOption<IssueSeverity | 'all'>[] = [
  { id: 'all', label: 'All' },
  { id: 'high', label: 'HIGH' },
  { id: 'medium', label: 'MEDIUM' },
  { id: 'low', label: 'LOW' },
  { id: 'unknown', label: 'UNKNOWN' },
];

const categoryTagColors: TagColor[] = [
  'blue',
  'green',
  'indigo',
  'purple',
  'pink',
  'teal',
  'orange',
  'yellow',
  'red',
  'gray',
];

const getCategoryTagColor = (name: string): TagColor => {
  const hash = name
    .trim()
    .toLowerCase()
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);

  return categoryTagColors[hash % categoryTagColors.length];
};

export function IssuesTableV2({
  issues,
  onIssueClick,
  emptyMessage,
  emptyState,
  tableContainerClassName,
  pagination,
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search issues by title or url',
  enableSelection = false,
  rowSelection,
  onRowSelectionChange,
  bulkSelection,
  sortBy,
  sortOrder,
  onSortChange,
  categories,
  onIssueMutated,
  severityFilter,
  onSeverityFilterChange,
  categoryFilter,
  onCategoryFilterChange,
  highlightedIssueId,
}: IssuesTableV2Props) {
  const propertyIds = React.useMemo(() => {
    const ids = Array.from(
      new Set(issues.map((issue) => issue.propertyId).filter((id): id is string => Boolean(id)))
    );
    ids.sort();
    return ids;
  }, [issues]);

  const canonicalPageIdsByProperty = React.useMemo(() => {
    const byProperty = new Map<string, string[]>();
    for (const issue of issues) {
      const canonicalPageId = issue.latestOccurrence?.canonicalPageId;
      if (!issue.propertyId || !canonicalPageId) continue;
      const current = byProperty.get(issue.propertyId) ?? [];
      if (!current.includes(canonicalPageId)) {
        current.push(canonicalPageId);
        byProperty.set(issue.propertyId, current);
      }
    }

    return Array.from(byProperty.entries())
      .map(([propertyId, ids]) => [propertyId, ids.sort()] as const)
      .sort(([a], [b]) => a.localeCompare(b));
  }, [issues]);

  const canonicalPagesByIdQuery = useQuery({
    queryKey: ['issues-table-canonical-pages-by-id', canonicalPageIdsByProperty],
    enabled: canonicalPageIdsByProperty.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const pageMap: Record<string, CanonicalPageSummary> = {};

      await Promise.all(
        canonicalPageIdsByProperty.map(async ([propertyId, canonicalPageIds]) => {
          try {
            const params = new URLSearchParams();
            params.set('ids', canonicalPageIds.join(','));
            const response = await fetch(
              `${API_URL}/properties/${propertyId}/canonical-pages/summary?${params}`,
              { credentials: 'include' }
            );
            if (!response.ok) {
              return;
            }
            const data = await response.json();
            const pages = (data.pages ?? []) as CanonicalPageSummary[];
            for (const page of pages) {
              pageMap[page.id] = page;
            }
          } catch {
            // Ignore per-property failures; table can fall back to occurrence URL.
          }
        })
      );

      return pageMap;
    },
  });

  const canonicalPagesById = canonicalPagesByIdQuery.data ?? {};
  const propertySummariesQuery = usePropertySummaries({ enabled: propertyIds.length > 0 });
  const propertyBaseUrlById = React.useMemo(() => {
    const map = new Map<string, string | null>();
    for (const property of propertySummariesQuery.data ?? []) {
      map.set(property.id, property.baseUrl ?? null);
    }
    return map;
  }, [propertySummariesQuery.data]);

  const columns = React.useMemo<ColumnDef<StockFeatures, IssueSummary>[]>(
    () => [
      {
        id: 'severity',
        accessorKey: 'severity',
        header: 'Severity',
        enableSorting: true,
        cell: ({ row }) => (
          <SeverityEditableBadge issue={row.original} onSuccess={onIssueMutated} />
        ),
        meta: { flex: 0.1, maxWidth: 125, minWidth: 125 },
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: 'Status',
        enableSorting: true,
        cell: ({ row }) => (
          <TagBadge
            name={formatStatusLabel(row.original.status)}
            color={badgeVariantToTagColor(
              issueStatusBadgeVariants[row.original.status] ?? 'default'
            )}
            hideDot
          />
        ),
        meta: { flex: 0.1, maxWidth: 100 },
      },
      {
        id: 'title',
        accessorKey: 'title',
        header: 'Title',
        enableSorting: true,
        cell: ({ row }) => {
          const isNew = wasFirstSeenInLast24Hours(row.original.firstSeenAt);
          const categoryLabel = row.original.category?.label;

          return (
            <Flex align="center" gap="xs" className="min-w-0">
              {isNew ? <TagBadge name="New" color="blue" size="sm" className="shrink-0" /> : null}
              {categoryLabel ? (
                <TagBadge
                  name={categoryLabel}
                  color={getCategoryTagColor(categoryLabel)}
                  size="sm"
                  className="max-w-[140px] shrink-0"
                />
              ) : null}
              {row.original.tags?.map((tag) => (
                <TagBadge
                  key={tag.id}
                  name={tag.name}
                  color={tag.color as TagColor}
                  size="sm"
                  className="shrink-0"
                />
              ))}
              <Text
                size="sm"
                weight="medium"
                className="truncate min-w-0 flex-1"
                title={row.original.title}
              >
                {row.original.title}
              </Text>
            </Flex>
          );
        },
        meta: { flex: 1, maxWidth: 600 },
      },
      {
        id: 'path',
        accessorFn: (row) => row.latestOccurrence?.primaryUrl ?? '',
        header: 'Page',
        enableSorting: true,
        cell: ({ row }) => {
          const primaryUrl = row.original.latestOccurrence?.primaryUrl;
          if (!primaryUrl) {
            return (
              <Text size="xs" className="text-muted-foreground font-mono">
                —
              </Text>
            );
          }
          const canonicalPageId = row.original.latestOccurrence?.canonicalPageId ?? null;
          const canonicalPage = canonicalPageId ? canonicalPagesById[canonicalPageId] : undefined;
          const canonicalTitle =
            canonicalPage?.title && canonicalPage.title.trim() ? canonicalPage.title.trim() : null;

          const pageHref = resolveIssuePageUrl({
            primaryUrl,
            canonicalPageUrl: canonicalPage?.url ?? null,
            propertyBaseUrl: propertyBaseUrlById.get(row.original.propertyId) ?? null,
          });
          const tooltipUrl = canonicalPage?.url ?? pageHref ?? primaryUrl;
          const label = canonicalTitle ?? getPageLabel(tooltipUrl);
          const path = getUrlPath(tooltipUrl);
          const isUrlLabel = !canonicalTitle;

          return (
            <TooltipTrigger delay={100} offset={8}>
              <Flex align="center" gap="xs" className="min-w-0">
                <Text
                  size="xs"
                  className={`text-muted-foreground truncate ${isUrlLabel ? 'font-mono' : ''}`}
                  title={tooltipUrl}
                >
                  {label}
                </Text>
                {pageHref ? (
                  <a
                    href={pageHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center text-muted-foreground hover:text-foreground"
                    aria-label="Open page in new tab"
                  >
                    <LinkIcon size={16} />
                  </a>
                ) : null}
              </Flex>
              <Tooltip>{path || tooltipUrl}</Tooltip>
            </TooltipTrigger>
          );
        },
        meta: { flex: 0.35, maxWidth: 800 },
      },
      {
        id: 'lastSeenAt',
        accessorKey: 'lastSeenAt',
        header: 'Last seen',
        enableSorting: true,
        cell: ({ row }) => {
          const lastSeen = row.original.lastSeenAt;
          if (!lastSeen) {
            return (
              <Text size="sm" className="text-muted-foreground">
                —
              </Text>
            );
          }
          return (
            <TooltipTrigger delay={100} offset={8}>
              <Flex direction="column" align="start" gap="xs" className="cursor-default">
                <Text size="xs" className="text-muted-foreground">
                  {formatTimestamp(lastSeen)}
                </Text>
              </Flex>
              <Tooltip>{formatExactTimestamp(lastSeen)}</Tooltip>
            </TooltipTrigger>
          );
        },
        meta: { flex: 0.2, maxWidth: 120 },
      },
    ],
    [canonicalPagesById, onIssueMutated, propertyBaseUrlById]
  );

  if (!issues || issues.length === 0) {
    if (emptyState) {
      return <>{emptyState}</>;
    }
    return (
      <div className="text-center py-12 border-2 border-dashed border-border rounded-lg">
        {emptyMessage ?? (
          <>
            <Warning size={48} className="mx-auto text-muted-foreground mb-4" />
            <Text className="text-muted-foreground">No issues found</Text>
            <Text size="sm" className="text-muted-foreground mt-1">
              Run analysis to detect new issues.
            </Text>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {enableSelection && bulkSelection ? (
        <BulkActionBar
          selectedCount={bulkSelection.selectedCount}
          itemLabel="issue"
          selectionMode={bulkSelection.selectionMode}
          deselectedCount={bulkSelection.deselectedCount}
          totalItems={bulkSelection.totalItems}
          onSelectAll={
            bulkSelection.canSelectAllResults ? bulkSelection.onSelectAllResults : undefined
          }
          onClearSelection={bulkSelection.onClearSelection}
          actions={[
            ...(bulkSelection.onRevalidateSelected
              ? [
                  {
                    label: 'Revalidate',
                    loadingLabel: 'Revalidating…',
                    variant: 'secondary' as const,
                    isLoading: bulkSelection.isRevalidateLoading,
                    onPress: bulkSelection.onRevalidateSelected,
                  },
                ]
              : []),
            {
              label: 'Archive',
              loadingLabel: 'Archiving…',
              variant: 'primary' as const,
              isLoading: bulkSelection.isArchiveLoading,
              onPress: bulkSelection.onArchiveSelected,
            },
          ]}
        />
      ) : null}

      {/* Filter toolbar */}
      {(onSeverityFilterChange || onCategoryFilterChange) && (
        <Flex align="center" gap="md" className="pb-2">
          <Text size="sm" className="text-muted-foreground">
            Filters:
          </Text>

          {onSeverityFilterChange && (
            <Flex align="center" gap="xs">
              <Text size="xs" className="text-muted-foreground">
                Severity
              </Text>
              <EditableBadge
                value={severityFilter ?? 'all'}
                options={severityFilterOptions}
                onChange={onSeverityFilterChange}
                getVariant={(value) =>
                  value === 'all' ? 'outline' : issueSeverityBadgeVariants[value]
                }
                formatLabel={(value) => (value === 'all' ? 'All' : formatSeverityLabel(value))}
                aria-label="Filter by severity"
              />
            </Flex>
          )}

          {onCategoryFilterChange && categories && categories.length > 0 && (
            <Flex align="center" gap="xs">
              <Text size="xs" className="text-muted-foreground">
                Category
              </Text>
              <EditableBadge
                value={categoryFilter ?? 'all'}
                options={[
                  { id: 'all', label: 'All' },
                  ...categories
                    .filter((c) => c.isActive)
                    .map((c) => ({ id: c.id, label: c.label })),
                ]}
                onChange={onCategoryFilterChange}
                variant={categoryFilter === 'all' || !categoryFilter ? 'outline' : 'secondary'}
                aria-label="Filter by category"
              />
            </Flex>
          )}
        </Flex>
      )}

      <DataTable
        columns={columns}
        data={issues}
        aria-label="Issues table"
        containerClassName={tableContainerClassName}
        searchable={Boolean(onSearchChange)}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        searchPlaceholder={searchPlaceholder}
        serverPagination={
          pagination
            ? {
                page: pagination.page,
                pageSize: pagination.pageSize,
                pageCount: Math.max(pagination.totalPages, 1),
                totalItems: pagination.totalItems,
                onPageChange: pagination.onPageChange,
                isLoading: pagination.isLoading,
              }
            : undefined
        }
        serverSorting={
          onSortChange
            ? {
                sortBy,
                sortOrder,
                onSortChange,
              }
            : undefined
        }
        getRowId={(issue) => issue.id}
        enableRowSelection={enableSelection}
        rowSelection={enableSelection ? rowSelection : undefined}
        onRowSelectionChange={enableSelection ? onRowSelectionChange : undefined}
        onRowClick={(issue) => {
          onIssueClick?.(issue);
        }}
        rowClassName={(row) =>
          row.original.id === highlightedIssueId
            ? 'bg-primary/5 ring-1 ring-inset ring-primary/20'
            : undefined
        }
      />
    </div>
  );
}
